import { createFileRoute, Link } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { api } from "@convex/_generated/api"
import type { Id } from "@convex/_generated/dataModel"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { ChevronRight, User } from "lucide-react"
import { CountryCell } from "./-components"

export const Route = createFileRoute("/(app)/app/results/players/$userId")({
  component: PlayerProfilePage,
})

// Ordered list of specialist topics
const SPECIALIST_TOPICS = [
  "History",
  "Pop Music",
  "Visual Arts",
  "Lifestyle",
  "Films & TV",
  "Geography",
  "Science",
  "Sports",
  "Literature",
  "Performing Arts",
] as const

function PlayerProfilePage() {
  const { userId } = Route.useParams()

  const profileData = useQuery(api.quizzes.handlers.getPlayerProfileStats, {
    userId: userId as Id<"users">,
  })

  if (profileData === undefined) {
    return (
      <div className="py-8 max-w-5xl mx-auto space-y-6">
        <Skeleton className="h-32 w-full" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
        <Skeleton className="h-[400px]" />
      </div>
    )
  }

  if (!profileData) {
    return (
      <div className="py-8 max-w-5xl mx-auto">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <User className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">Player Not Found</h3>
            <p className="text-sm text-muted-foreground">
              This player profile does not exist.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const { user, stats, specialistStats, teams } = profileData

  // Get initials for avatar
  const initials = user.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)

  // Calculate circuit total (sum of best 3 from each topic, excluding Mystery Box)
  const circuitTotal = Object.entries(specialistStats)
    .filter(([topic]) => topic !== "Mystery Box")
    .reduce((sum, [, stats]) => sum + stats.best3, 0)

  return (
    <div className="py-8 max-w-5xl mx-auto space-y-6">
      {/* Breadcrumbs */}
      <nav className="flex items-center text-sm text-muted-foreground">
        <Link to="/app/results" className="hover:text-foreground transition-colors">
          Results
        </Link>
        <ChevronRight className="h-4 w-4 mx-2" />
        <span className="text-foreground font-medium">{user.name}</span>
      </nav>

      {/* Header / Profile Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold shrink-0">
              {initials}
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold">{user.name}</h1>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-muted-foreground">
                <CountryCell country={user.country} />
                {user.division && (
                  <>
                    <span className="text-muted-foreground/50">•</span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200">
                      Division {user.division}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground mb-1">Total Quizzes</div>
            <div className="text-2xl font-bold">{stats.totalQuizzes}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground mb-1">Correct Rate</div>
            <div className="text-2xl font-bold">
              {(stats.correctRate * 100).toFixed(1)}%
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground mb-1">Division</div>
            <div className="text-2xl font-bold text-emerald-600">
              {user.division ?? "—"}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground mb-1">Circuit Total</div>
            <div className="text-2xl font-bold">{circuitTotal}</div>
          </CardContent>
        </Card>
      </div>

      {/* Two Column Layout */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Teams/Pairs */}
        <Card>
          <CardHeader>
            <CardTitle>Teams & Pairs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {teams.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                Not a member of any team or pair.
              </p>
            ) : (
              teams.map((team) => (
                <Link
                  key={team.teamId}
                  to="/app/results/teams/$teamId"
                  params={{ teamId: team.teamId }}
                  className="block p-3 rounded-lg bg-muted/50 border hover:border-border transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium">{team.teamName}</span>
                      <span className="text-xs text-muted-foreground ml-2 capitalize">
                        ({team.teamType === "teams" ? "Team" : "Pair"})
                      </span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </Link>
              ))
            )}
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <Card>
          <CardHeader>
            <CardTitle>Performance Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Total Correct</span>
                <span className="font-semibold">{stats.totalCorrect} / {stats.totalQuestions}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Topics Played</span>
                <span className="font-semibold">{Object.keys(specialistStats).length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Teams & Pairs</span>
                <span className="font-semibold">{teams.length}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Specialist Performance Table */}
      {Object.keys(specialistStats).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Specialist Performance</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[150px]">Topic</TableHead>
                  <TableHead className="text-center">Games</TableHead>
                  <TableHead className="text-right bg-amber-50/50 dark:bg-amber-900/10 font-bold">
                    Best 3
                  </TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Best Single</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {SPECIALIST_TOPICS.map((topic) => {
                  const topicStats = specialistStats[topic]
                  if (!topicStats) return null
                  const total = topicStats.scores.reduce((sum, s) => sum + s, 0)
                  return (
                    <TableRow key={topic}>
                      <TableCell className="font-medium">{topic}</TableCell>
                      <TableCell className="text-center tabular-nums">
                        {topicStats.gamesPlayed}
                      </TableCell>
                      <TableCell className="text-right font-bold tabular-nums bg-amber-50/50 dark:bg-amber-900/10">
                        {topicStats.best3}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {total}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {topicStats.bestSingle}
                      </TableCell>
                    </TableRow>
                  )
                })}
                {/* Circuit Total Row */}
                <TableRow className="border-t-2">
                  <TableCell className="font-semibold">Circuit Total</TableCell>
                  <TableCell className="text-center tabular-nums">
                    {Object.values(specialistStats).reduce(
                      (sum, s) => sum + s.gamesPlayed,
                      0
                    )}
                  </TableCell>
                  <TableCell className="text-right font-bold tabular-nums bg-amber-100 dark:bg-amber-900/30">
                    {circuitTotal}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {Object.values(specialistStats).reduce(
                      (sum, s) => sum + s.scores.reduce((a, b) => a + b, 0),
                      0
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    —
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
